import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'

// Create axios instance
export const apiClient = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies in requests
})

// Request interceptor - cookies are sent automatically
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Cookies are automatically sent with withCredentials: true
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Keep track of whether we're currently refreshing
let isRefreshing = false
let failedQueue: Array<{
  resolve: (value?: any) => void
  reject: (error?: any) => void
}> = []

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  
  failedQueue = []
}

// Response interceptor to handle token refresh and permission errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any

    // Don't handle auth for auth endpoints themselves
    const isAuthEndpoint = originalRequest.url?.includes('/auth/') || 
                          originalRequest.url?.includes('/refresh')
    if (isAuthEndpoint) {
      return Promise.reject(error)
    }

    // Handle 403 permission errors
    if (error.response?.status === 403) {
      // Emit permission error event
      window.dispatchEvent(new CustomEvent('permission-error', { 
        detail: { 
          message: error.response.data || 'Access denied',
          url: originalRequest.url 
        } 
      }))
      return Promise.reject(error)
    }

    // Handle 401 errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If we're already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(() => {
          return apiClient(originalRequest)
        }).catch((err) => {
          return Promise.reject(err)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        // Attempt to refresh token using cookie
        await axios.post('/api/v1/auth/refresh', {}, { 
          withCredentials: true 
        })
        
        // Process queued requests
        processQueue(null)
        isRefreshing = false
        
        // Retry the original request
        return apiClient(originalRequest)
      } catch (refreshError) {
        // Refresh failed
        processQueue(refreshError, null)
        isRefreshing = false
        
        // Only redirect to login if we're not already there and not on a public route
        const publicPaths = ['/login', '/register', '/forgot-password', '/invite']
        const isPublicPath = publicPaths.some(path => window.location.pathname.startsWith(path))
        
        if (!isPublicPath) {
          window.location.href = '/login'
        }
        
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

/**
 * Extracts the backend `detail` message from a failed API call, if present.
 *
 * FastAPI error responses carry a human-readable `detail` string; callers use
 * this to surface it in a toast and fall back to their own message otherwise.
 */
export function apiErrorDetail(error: unknown): string | undefined {
  const detail = (error as { response?: { data?: { detail?: unknown } } } | null)
    ?.response?.data?.detail
  return typeof detail === 'string' ? detail : undefined
}

/** Field-keyed messages from a FastAPI 422; reads only `loc`/`msg`, never `input`. */
export function apiFieldErrors(error: unknown): Record<string, string> {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (!Array.isArray(detail)) return {}
  return Object.fromEntries(
    detail.flatMap((d: { loc?: unknown[]; msg?: string }) => {
      const field = Array.isArray(d.loc) ? String(d.loc[d.loc.length - 1]) : ''
      return field && d.msg ? [[field, d.msg]] : []
    })
  )
}

// Dashboard API
export const dashboardApi = {
  getEnergyMetrics: () => apiClient.get('/dashboard/energy'),
}
